// filepath: apps/web/src/design-system/Tabs.tsx
//
// FR-UI-CORE-3 -- Tabs primitive ported from the reference JSX in
// docs/guides/design system/components/navigation/Tabs.jsx.
//
// Contract (apps/web/src/design-system/Tabs.d.ts):
//   - tabs:     Tab[]   -- { id, label, count? }
//   - activeId: string  -- currently selected tab id
//   - onChange: (id: string) => void
//
// Tokens via var(--semantic-alias) only -- no raw hex.

import React from "react";

export interface Tab {
  id: string;
  label: string;
  count?: number;
}

export interface TabsProps {
  tabs?: readonly Tab[];
  activeId?: string;
  onChange?: (id: string) => void;
  /** Test hook forwarded to the root container. */
  "data-testid"?: string;
}

export function Tabs({
  tabs = [],
  activeId,
  onChange,
  "data-testid": testId,
}: TabsProps): React.ReactElement {
  return (
    <div
      data-testid={testId}
      className="ds-tabs"
      style={{
        display: "flex",
        gap: "24px",
        borderBottom: "2px solid var(--border-default)",
        fontFamily: "var(--font-sans)",
      }}
    >
      {tabs.map((t) => {
        const active = t.id === activeId;
        return (
          <button
            key={t.id}
            type="button"
            data-testid={`${testId ?? "tabs"}-tab-${t.id}`}
            data-active={active ? "true" : "false"}
            onClick={() => onChange?.(t.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "10px 2px",
              marginBottom: "-2px",
              border: "none",
              borderBottom: active
                ? "2px solid var(--color-primary)"
                : "2px solid transparent",
              background: "transparent",
              cursor: "pointer",
              color: active ? "var(--color-primary)" : "var(--text-secondary)",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-medium)",
              fontFamily: "inherit",
            }}
          >
            {t.label}
            {t.count !== undefined && t.count !== null ? (
              <span
                style={{
                  padding: "1px 6px",
                  borderRadius: "var(--radius-full)",
                  fontSize: "var(--text-xs)",
                  background: active
                    ? "var(--color-primary-subtle)"
                    : "var(--gray-100)",
                  color: active
                    ? "var(--color-primary-subtle-text)"
                    : "var(--text-secondary)",
                }}
              >
                {t.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
